import Dexie from "dexie";

// Device storage: kv (session/product/receipt-block caches), creds (salted PBKDF2 hashes only), outbox (offline sales).
export const ldb = new Dexie("samrtin-offline");
ldb.version(1).stores({ kv: "key", creds: "identifier, user_id", outbox: "client_sale_id, business_id, status, created_at" });

export const kvGet = async (key) => (await ldb.kv.get(key))?.value;
export const kvSet = (key, value) => ldb.kv.put({ key, value });

const DEVICE_KEY = "samrtin_device";
export function device() {
  let d = null;
  try {
    d = JSON.parse(localStorage.getItem(DEVICE_KEY) || "null");
  } catch {
    d = null;
  }
  if (!d?.id) {
    const id = crypto.randomUUID();
    d = { id, code: id.replace(/-/g, "").slice(0, 4).toUpperCase() };
    localStorage.setItem(DEVICE_KEY, JSON.stringify(d));
  }
  return d;
}

export const utcToday = () => new Date().toISOString().slice(0, 10);

const strip = ({ token, offline, offlineLogin, ...s }) => s;
export const cacheSession = async (s) => {
  const clean = strip(s);
  await kvSet(`session:${s.user.id}`, clean);
  await ldb.creds.where("user_id").equals(s.user.id).modify({ session: clean });
};
export const cachedSession = (userId) => kvGet(`session:${userId}`);

export function jwtSub(token) {
  try {
    return JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))).sub;
  } catch {
    return null;
  }
}

export const isWellFormedSale = (s) =>
  !!s && typeof s === "object" && Array.isArray(s.lines) && s.lines.length > 0 &&
  s.lines.every((l) => l && typeof l.qty === "number" && (l.allocations == null || Array.isArray(l.allocations))) &&
  !!s.payment && typeof s.payment === "object" && !!s.totals && typeof s.totals.total === "number";

// Locally visible stock = last server snapshot minus sales still waiting on this device.
export function applyPending(raw, items, branchId, pharmacy) {
  const mine = items.filter((i) => i.branch_id === branchId && isWellFormedSale(i.sale));
  if (!raw || !Array.isArray(raw.products) || !mine.length) return raw;
  const byBatch = {}, byProduct = {}, byLot = {};
  mine.forEach((i) =>
    i.sale.lines.forEach((ln) => {
      byProduct[ln.product_id] = (byProduct[ln.product_id] || 0) + ln.qty;
      (ln.allocations || []).forEach((a) => {
        if (!a) return;
        byBatch[a.batch_id] = (byBatch[a.batch_id] || 0) + (Number(a.qty) || 0);
        if (a.lot_id) byLot[a.lot_id] = (byLot[a.lot_id] || 0) + (Number(a.qty) || 0);
      });
    })
  );
  const products = raw.products.map((p0) => {
    const p = Array.isArray(p0.lots) && p0.lots.some((l) => byLot[l.id]) ? { ...p0, lots: p0.lots.map((l) => (byLot[l.id] ? { ...l, qty: l.qty - byLot[l.id] } : l)) } : p0;
    if (pharmacy) {
      if (!p.batches?.some((b) => byBatch[b.id])) return p;
      const batches = p.batches.map((b) => (byBatch[b.id] ? { ...b, qty: b.qty - byBatch[b.id] } : b));
      return { ...p, batches, valid_stock: batches.filter((b) => !b.expired).reduce((a, b) => a + Math.max(0, b.qty), 0), negative_stock: batches.some((b) => b.qty < 0) };
    }
    if (p.stock == null || !byProduct[p.id]) return p;
    const stock = p.stock - byProduct[p.id];
    return { ...p, stock, negative_stock: stock < 0 };
  });
  return { ...raw, products };
}

export const productsKey = (s) => `products:${s.business.id}:${s.branch.id}`;
